import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import VideoBackground from '../../../components/base/VideoBackground';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';

const ReportingAnalyticsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-blue-600 bg-clip-text text-transparent mb-6">
            Reporting & Analytics
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Make data-driven decisions with comprehensive analytics and reporting that reveal insights to optimize your marketing performance.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
            Analytics Benefits for Small Businesses
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-lightbulb-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Data-Driven Insights</h3>
              <p className="text-gray-600">Make informed decisions based on real performance data and customer behavior.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-funds-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">ROI Optimization</h3>
              <p className="text-gray-600">Identify which marketing efforts deliver the best return on investment.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-rocket-2-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Growth Acceleration</h3>
              <p className="text-gray-600">Identify opportunities for growth and scale successful strategies.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
            Our Analytics Services Include
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-emerald-100 to-teal-100 p-6 rounded-xl">
              <i className="ri-dashboard-3-line text-3xl text-emerald-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Custom Dashboards</h4>
              <p className="text-sm text-gray-600">Real-time performance dashboards tailored to your KPIs</p>
            </div>
            <div className="bg-gradient-to-br from-teal-100 to-blue-100 p-6 rounded-xl">
              <i className="ri-line-chart-line text-3xl text-teal-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Traffic Analysis</h4>
              <p className="text-sm text-gray-600">Detailed website traffic and user behavior insights</p>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-6 rounded-xl">
              <i className="ri-pie-chart-2-line text-3xl text-blue-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Conversion Tracking</h4>
              <p className="text-sm text-gray-600">Monitor and optimize your conversion funnel</p>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-6 rounded-xl">
              <i className="ri-calendar-event-line text-3xl text-purple-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Monthly Reports</h4>
              <p className="text-sm text-gray-600">Comprehensive monthly performance reports</p>
            </div>
            <div className="bg-gradient-to-br from-pink-100 to-red-100 p-6 rounded-xl">
              <i className="ri-presentation-line text-3xl text-pink-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Strategy Recommendations</h4>
              <p className="text-sm text-gray-600">Actionable insights and optimization suggestions</p>
            </div>
            <div className="bg-gradient-to-br from-red-100 to-orange-100 p-6 rounded-xl">
              <i className="ri-eye-line text-3xl text-red-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Competitive Analysis</h4>
              <p className="text-sm text-gray-600">Track performance against your competitors</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Links */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-xl font-bold mb-6 text-gray-800">Follow Our Analytics Insights</h3>
          <div className="flex justify-center space-x-6">
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-facebook-fill text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-instagram-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-twitter-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-linkedin-fill text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-emerald-500 via-teal-500 to-blue-500 rounded-3xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Unlock Your Data's Potential?</h2>
          <p className="text-xl mb-8">Let's transform your data into actionable insights that drive business growth!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/consultation"
              className="bg-white text-teal-600 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors"
            >
              Book Free Consultation
            </Link>
            <Link 
              to="/quote"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-teal-600 transition-colors"
            >
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReportingAnalyticsPage;